import {
  DEFAULT_GENERATION_MODEL,
  type AttributeTreeNode,
  type GenerationSettings,
} from '../state/useGenerationState';

/** テンプレートモードのセクションキー。
 *  backend の prompt_attributes_service.TEMPLATE_SECTION_KEY と揃えること。 */
export const TEMPLATE_SECTION_KEY = 'template';

/** 移行期間中のフォールバック用 option key 集合。
 *  正本は DB (metadata.capture_required)。ただし migration 未適用・OEM 非表示・
 *  古い保存データ復元などで選択中 option ノードの metadata が読めないケースでも、
 *  最低限キャプチャ必須と判定できるよう key ベースの保険を残す。
 *  capture_required が全環境の対象ノードに行き渡ったら削除してよい。 */
export const CAPTURE_REQUIRED_FALLBACK_KEYS = new Set(['poster']);

/** option ノード単体が metadata でキャプチャ必須指定されているか (DB 駆動の正本)。 */
export function isCaptureRequiredNode(node: AttributeTreeNode | undefined | null): boolean {
  return node?.metadata?.capture_required === true;
}

/** metadata (正本) と optionKey フォールバックの両方を見てキャプチャ必須か判定する。
 *  node が tree から取れない (非表示/未適用/古いデータ) ケースは optionKey で救済する。 */
export function isCaptureRequiredOption(
  node: AttributeTreeNode | undefined | null,
  optionKey: string | null | undefined,
): boolean {
  if (isCaptureRequiredNode(node)) return true;
  return optionKey != null && CAPTURE_REQUIRED_FALLBACK_KEYS.has(optionKey);
}

/** ホーム (テンプレート一覧) → /studio/image 遷移時にテンプレ初期選択を受け渡す
 *  localStorage キー。書き込みはホーム側、消費 (読み取り + 削除) は GenerationWorkspace。 */
export const PENDING_TEMPLATE_SELECTION_KEY = 'pending_template_selection';

export type PendingTemplateSelection = { groupKey: string; optionKey: string };

/** 受け渡されたテンプレ初期選択を消費する (読めたかどうかに関わらずキーは消す)。 */
export function consumePendingTemplateSelection(): PendingTemplateSelection | null {
  const raw = localStorage.getItem(PENDING_TEMPLATE_SELECTION_KEY);
  if (!raw) return null;
  localStorage.removeItem(PENDING_TEMPLATE_SELECTION_KEY);
  try {
    const parsed = JSON.parse(raw) as Partial<PendingTemplateSelection>;
    if (typeof parsed.groupKey === 'string' && typeof parsed.optionKey === 'string') {
      return { groupKey: parsed.groupKey, optionKey: parsed.optionKey };
    }
  } catch {
    // 壊れた値は無視 (キーは削除済み)
  }
  return null;
}

/** 公開ホーム (未ログイン) で「画像を生成」→ ログイン誘導する際に、選択中テンプレと
 *  URL 入力を退避する sessionStorage キー。書き込みはログイン誘導時、復元はホーム側。
 *  urlKind を含めるのは必須: 入力欄の表示値は templateReferenceUrl と urlKind の
 *  プレフィックス前方一致の組み合わせで決まるため、URL だけ復元すると
 *  ホームページ系 URL が空欄に見える (URL からの導出は https://x.com/… が
 *  両プレフィックスにマッチして曖昧なので、退避時の state をそのまま持ち回る)。 */
export const PENDING_LOGIN_GENERATION_KEY = 'pending_login_generation';

export type PendingLoginGeneration = {
  groupKey: string;
  optionKey: string;
  templateReferenceUrl: string;
  urlKind: 'x' | 'instagram' | 'homepage';
};

export function stashPendingLoginGeneration(payload: PendingLoginGeneration): void {
  try {
    sessionStorage.setItem(PENDING_LOGIN_GENERATION_KEY, JSON.stringify(payload));
  } catch {
    // ストレージ不可 (プライベートモード等) は退避なしで遷移 (先頭テンプレ自動選択に劣化)
  }
}

/** 退避されたログイン前の選択状態を読む。remove=true でキーも消す
 *  (ログイン前のブラウザバック復帰でも選択を保持できるよう、削除はログイン済みの
 *  復元時のみ行う)。 */
export function readPendingLoginGeneration(remove: boolean): PendingLoginGeneration | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_LOGIN_GENERATION_KEY);
    if (raw && remove) sessionStorage.removeItem(PENDING_LOGIN_GENERATION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingLoginGeneration>;
    if (
      typeof parsed.groupKey === 'string' &&
      typeof parsed.optionKey === 'string' &&
      typeof parsed.templateReferenceUrl === 'string' &&
      (parsed.urlKind === 'x' || parsed.urlKind === 'instagram' || parsed.urlKind === 'homepage')
    ) {
      return {
        groupKey: parsed.groupKey,
        optionKey: parsed.optionKey,
        templateReferenceUrl: parsed.templateReferenceUrl,
        urlKind: parsed.urlKind,
      };
    }
  } catch {
    // 壊れた値は無視
  }
  return null;
}

/** ネスト内から key 一致のノードを再帰探索。 */
export function findNodeByKey(
  children: AttributeTreeNode[] | undefined,
  key: string,
): AttributeTreeNode | undefined {
  if (!children) return undefined;
  for (const child of children) {
    if (child.key === key) return child;
    const found = findNodeByKey(child.children, key);
    if (found) return found;
  }
  return undefined;
}

/** キャプチャ必須テンプレ選択時に transient 適用する初期設定
 *  (GPT Image 2 + メディアプラットフォーム指定なし)。
 *  applyTransientSettings とセットで使い、localStorage のモデル選好・
 *  プラットフォーム記憶は汚さない。配列が state に取り込まれるため、
 *  共有定数でなく毎回新しいオブジェクトを返す。 */
export function captureTemplateTransientSettings(): Partial<GenerationSettings> {
  return {
    generation_model: DEFAULT_GENERATION_MODEL,
    media_platform_id: null,
    media_menu_id: null,
    media_spec_id: null,
    media_guidelines: [],
    media_menu_guidelines: [],
  };
}

/** 上記適用時に applyTransientSettings へ渡す「永続化をスキップするキー」 */
export const CAPTURE_TEMPLATE_TRANSIENT_KEYS = ['generation_model', 'media_platform_id'];

/** 選択中のテンプレート option を返す。node は tree に無ければ null になるが、
 *  optionKey は settings 由来なので常に得られる (フォールバック判定に使う)。 */
export function findActiveTemplateOption(
  attributeTree: AttributeTreeNode[] | undefined,
  attributes: Record<string, unknown> | undefined,
): { optionKey: string; node: AttributeTreeNode | null } | null {
  for (const node of attributeTree ?? []) {
    if (node.type !== 'group' || node.section !== TEMPLATE_SECTION_KEY) continue;
    const value = attributes?.[node.key];
    if (typeof value === 'string' && value && value !== 'none') {
      return { optionKey: value, node: findNodeByKey(node.children, value) ?? null };
    }
  }
  return null;
}

/** 選択中テンプレートが URL キャプチャ / 参照画像必須かどうか。 */
export function isCaptureRequiredTemplateActive(
  attributeTree: AttributeTreeNode[] | undefined,
  attributes: Record<string, unknown> | undefined,
): boolean {
  const active = findActiveTemplateOption(attributeTree, attributes);
  if (!active) return false;
  return isCaptureRequiredOption(active.node, active.optionKey);
}
